import React, { useState, useEffect, useCallback } from 'react';
import Modal from './Modal';
import { feedbackApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const errText = (err) =>
  err.response?.data?.error ||
  (err.response?.status === 429 ? 'Too many attempts. Please wait a minute and try again.'
                               : 'Something went wrong — please try again.');

const CATEGORIES = [
  { value: 'bug',   label: '🐞 Something is broken' },
  { value: 'idea',  label: '💡 Idea / request' },
  { value: 'other', label: '💬 Other' },
];

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function FeedbackModal({ onClose, context }) {
  const { user } = useAuth();
  // 'form' | 'sent' | 'admin'
  const [view, setView]       = useState('form');
  const [category, setCategory] = useState('idea');
  const [message, setMessage] = useState('');
  const [error, setError]     = useState('');
  const [busy, setBusy]       = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      await feedbackApi.submit({ category, message: message.trim(), context: context || null });
      setView('sent');
    } catch (err) { setError(errText(err)); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={view === 'admin' ? 'Beta feedback' : 'Send feedback'} onClose={onClose} wide={view === 'admin'}>
      {view === 'form' && (
        <form onSubmit={submit}>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 14, lineHeight: 1.5 }}>
            Trigger Budget is in <strong>beta</strong> — your feedback shapes what we fix and build next.
            Found a bug or have an idea? Let us know.
          </div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label>Type</label>
            <select className="field-input" style={{ width: '100%' }}
              value={category} onChange={e => setCategory(e.target.value)}>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label>Your feedback</label>
            <textarea className="field-input" rows={5} autoFocus maxLength={4000}
              style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
              placeholder="What happened, or what would make this better?"
              value={message} onChange={e => setMessage(e.target.value)} />
          </div>
          {error && (
            <div style={{ background: 'var(--red-bg)', border: '1px solid var(--red)', color: 'var(--red)',
              borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 14 }}>{error}</div>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {user?.admin && (
              <button type="button" className="btn btn-sm" onClick={() => setView('admin')}>
                View all
              </button>
            )}
            <div style={{ flex: 1 }} />
            <button type="button" className="btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy || !message.trim()}>
              {busy ? 'Sending…' : 'Send feedback'}
            </button>
          </div>
        </form>
      )}

      {view === 'sent' && (
        <div>
          <div style={{ fontSize: 13, marginBottom: 6, fontWeight: 700, color: 'var(--green)' }}>
            Thanks — got it.
          </div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.5 }}>
            Your feedback was sent. We review everything that comes in during the beta.
          </div>
          <button className="btn btn-primary" style={{ width: '100%' }} onClick={onClose}>Done</button>
        </div>
      )}

      {view === 'admin' && <AdminList onBack={() => setView('form')} />}
    </Modal>
  );
}

function AdminList({ onBack }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try { setItems((await feedbackApi.getAll()).data); }
    catch (err) { setError(errText(err)); setItems([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cycle = { new: 'reviewed', reviewed: 'done', done: 'new' };
  const colour = { new: 'var(--red)', reviewed: 'var(--text2)', done: 'var(--green)' };

  const advance = async (item) => {
    const next = cycle[item.status] || 'new';
    setItems(list => list.map(i => i.id === item.id ? { ...i, status: next } : i));
    try { await feedbackApi.setStatus(item.id, next); }
    catch (err) { setError(errText(err)); load(); }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <button className="btn btn-sm" onClick={onBack}>‹ Back</button>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>
          {items ? `${items.length} item${items.length === 1 ? '' : 's'}` : ''}
        </span>
      </div>
      {error && (
        <div style={{ background: 'var(--red-bg)', border: '1px solid var(--red)', color: 'var(--red)',
          borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{error}</div>
      )}
      {!items && <div className="spinner" style={{ margin: '24px auto' }} />}
      {items && items.length === 0 && !error && (
        <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', padding: 24 }}>
          No feedback yet.
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 420, overflowY: 'auto' }}>
        {items?.map(item => (
          <div key={item.id} style={{ background: 'var(--bg2)', borderRadius: 8, padding: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{item.category}</span>
              <span style={{ fontSize: 11, color: 'var(--text3)' }}>{item.userEmail}</span>
              {item.context && <span style={{ fontSize: 11, color: 'var(--text3)' }}>· {item.context}</span>}
              <div style={{ flex: 1 }} />
              <button className="btn btn-sm" style={{ color: colour[item.status] || 'var(--text2)' }}
                title="Click to change status" onClick={() => advance(item)}>{item.status}</button>
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{item.message}</div>
            <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 6 }}>{fmtDate(item.createdAt)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
